import { useArtifactDetail } from "@/api/artifacts";
import { EmptyState } from "@/components/layout/EmptyState";
import type { RunVisualView } from "@/components/runs/run-visual-views";
import { Skeleton } from "@/components/ui/skeleton";
import { MoleculeViewerPanel } from "@/components/viz/MoleculeViewerPanel";

/**
 * The two renderable halves of a run's VISUAL, split out of the old
 * `RunVisualPanel` (flowstack#950 Part B, #956) when #991 turned the
 * workspace into a mode switch.
 *
 * What changed and why: the panel used to own three things at once — the
 * view SELECTOR, the empty states, and the molecule. #991 promotes the
 * selector to the workspace's main-area mode switch (a run's visual is no
 * longer a thing tucked in a 26rem column; it can BE the main area), and both
 * the main area and the side panel now need to draw the same molecule. A
 * component that owned its own selection could not serve both, so what is
 * left here is the drawing, and the workspace owns which of it to draw.
 *
 * The properties #950/#956 established are unchanged, and this file is still
 * where two of them live:
 *
 * - **A view whose data does not exist yet is PENDING, never empty-and-silent
 *   and never a failure** — three distinguishable states, and a fourth
 *   ("produced, failed to load") that each view raises from its own fetch.
 *   Collapsing any of them into another is flowstack#923.
 * - **A step pinned to nothing renderable is its OWN state**, not the run's:
 *   falling back to the run subject would claim the step operated on the
 *   ligand when it may have consumed a force field or a topology.
 */

/**
 * The empty state for a view that is not `ready`.
 *
 * Deliberately a separate export rather than a branch inside the molecule:
 * the workspace shows this in the MAIN area when the picked mode's data has
 * not arrived, which is a different place from where the structure renders.
 */
export function RunVisualUnavailable({
  view,
  runIsLive,
}: {
  view: RunVisualView;
  runIsLive: boolean;
}): JSX.Element {
  const { title, description } = describeUnavailable(view, runIsLive);
  return <EmptyState title={title} description={description} />;
}

/**
 * Which sentence a not-ready view gets, and why it is a function rather than
 * nested ternaries (Rupert PR-999 r1).
 *
 * The states are not a two-way branch: *pinned-empty*, *pending while live*,
 * *pending but merely unread*, and *absent* are four different claims with
 * four different remedies, and the file's own header notes a fifth
 * ("produced, failed to load") that each view raises from its own fetch. Read
 * as a ternary chain that is easy to mis-nest on the next addition, and
 * mis-nesting it produces exactly the flowstack#923 conflation — a wrong but
 * plausible sentence about someone's run.
 *
 * Exported for its own test, so each branch is pinned by the claim it makes
 * rather than only through a rendered component.
 */
export function describeUnavailable(
  view: RunVisualView,
  runIsLive: boolean
): { title: string; description: string } {
  const noun = view.label.toLowerCase();
  // A step pinned to nothing renderable is its OWN state: falling back to the
  // run subject would claim the step operated on the ligand when it may have
  // consumed a force field or a topology.
  if (view.id === "molecule" && view.pinnedToNode) {
    return {
      title: `${view.pinnedToNode} has no molecule input.`,
      description:
        "This step consumes something with no structure to draw — a force field, a topology or a log. Clear the selection to see the run's molecule.",
    };
  }
  if (view.availability === "pending") {
    return {
      title: `No ${noun} yet.`,
      description: runIsLive
        ? "Views appear here as the flow's steps publish their outputs."
        : "Still reading this run's outputs.",
    };
  }
  return {
    title: `This run produced no ${noun}.`,
    description:
      "Not every step has something to draw — a force-field bundle, a topology or a log has no visual.",
  };
}

/**
 * What the molecule section CALLS what it is drawing, and the sentence beneath
 * it when that needs one.
 *
 * Three states, and they are three claims rather than a default plus two
 * decorations:
 *
 * - **`Input to <node>`** — the pin (flowstack#956). This step consumed this
 *   structure; the panel is the answer to "what did it operate on".
 * - **`Run molecule`** — the rail's fallback (flowstack#1237). The selected
 *   step consumed no structure, so what is on screen is the run's subject and
 *   the heading says so. The heading carries the CLAIM (whose molecule this is)
 *   and stays short enough to survive the rail's truncation; the note carries
 *   the reason, because a fallback the reader cannot see is a substitution
 *   made silently, which is the thing `deriveRunVisualViews` refuses.
 * - **`Molecule`** — nothing selected. The run's subject, with no step to
 *   contrast it against.
 *
 * A function, and exported, for the reason `describeUnavailable` is one: these
 * are sentences about someone's run, and each should be pinnable by the claim
 * it makes rather than only through a mounted component. Shared by the rail and
 * the main pane so the two cannot drift — the main pane's view never carries
 * `subjectFallbackFromNode`, so it simply never reaches the middle branch.
 */
export function describeMolecule(view: RunVisualView): { heading: string; note: string | null } {
  if (view.pinnedToNode) return { heading: `Input to ${view.pinnedToNode}`, note: null };
  if (view.subjectFallbackFromNode) {
    return {
      heading: "Run molecule",
      note:
        `${view.subjectFallbackFromNode} consumed no structure to draw, ` +
        "so this is the molecule the run is about — not that step's input.",
    };
  }
  return { heading: "Molecule", note: null };
}

/**
 * The molecule itself.
 *
 * Fetches the artifact detail by CONTENT HASH and hands it to the shared
 * `MoleculeViewerPanel`, which owns the lazy boundary around 3Dmol.js and the
 * type→payload selection. Same route Part A's submission preview takes, for
 * the same reason: the only thing this component can render is the artifact
 * the catalog holds, so there is no code path by which it could show
 * something other than what the run will actually compute on.
 */
export function RunSubjectMolecule({
  contentHash,
  fill,
  siblingCount,
  heading = "Molecule",
  note = null,
}: {
  contentHash: string | null;
  fill: boolean;
  siblingCount: number;
  /** Says WHOSE structure this is; the workspace passes `describeMolecule`. */
  heading?: string;
  /** The sentence under the viewer when the heading alone cannot carry the
   * whole claim — the rail's fallback says which step it fell back from. */
  note?: string | null;
}): JSX.Element {
  const { data, isLoading, error } = useArtifactDetail(contentHash);

  if (isLoading) return <Skeleton className={fill ? "h-full w-full" : "h-[320px] w-full"} />;
  if (error) {
    return (
      <EmptyState
        title="Could not load the molecule."
        description={error instanceof Error ? error.message : "Unknown error."}
      />
    );
  }
  if (!data) {
    return (
      <EmptyState
        title="Could not load the molecule."
        description="The catalog returned no record for this artifact."
      />
    );
  }
  return (
    <div className={fill ? "flex min-h-0 flex-1 flex-col" : undefined}>
      <MoleculeViewerPanel artifact={data} heading={heading} fill={fill} />
      {note !== null && (
        <p className="mt-1 text-[11px] text-muted-foreground" data-testid="molecule-note">
          {note}
        </p>
      )}
      {siblingCount > 1 && (
        // A FANNED step consumes a different artifact per child (one
        // fragment each). Showing one without saying so would assert
        // "this is what the step ran on" about a step that ran on N
        // things.
        <p className="mt-1 text-[11px] text-muted-foreground">
          Showing 1 of {siblingCount} inputs this step consumed across its children.
        </p>
      )}
    </div>
  );
}
